import { useState, useEffect, useCallback } from "react";
import { rtdb } from "../../services/firebase";
import { USER_CHANNEL_UNSEEN_COUNT, UserChannel } from "../../types/db/chat";
import { UserChannelService } from "../../types/services/chat";

const userChannelService = (new UserChannelService(rtdb));

/**
 * Hook which takes care of fetching and updating the current user channel
 * @param userId The user ID
 * @param channelId The optional selected channel id
 * @returns the user channel
 */
export const useUserCurrentChannel = (userId: string, channelId: string): {
    /** The list of threads in the inbox */
    channel: UserChannel | null;
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
    /** Mark the current user channel as read */
    markAsRead: () => Promise<void>;
    /** Save a draft in the current user channel */
    saveDraftMessage: (message: string) => Promise<void>;
} => {
    const [isLoading, setIsLoading] = useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, _setError] = useState('');
    const [channel, setChannel] = useState<UserChannel | null>(null);

    useEffect(() => {
        if (!userId || !channelId) {
            setIsLoading(false);
            //in the case of user not defined->no channels
            setChannel(null);
            return;
        }

        //update the current listing with the listing info from BE
        setIsLoading(true)
        //subscribe to any added child of that user's channel
        const fetchUserChannel = userChannelService.getChildRef(userId, channelId);

        fetchUserChannel
            .on("value", (snapshot) => {
                const userChannelInfo: UserChannel | null = snapshot.val();
                setIsLoading(false)
                if (!userChannelInfo) {
                    return;
                }

                // append the item on the list
                setChannel(userChannelInfo);
            });

        return () => {
            //unsubscribe to all listener for the user channel
            fetchUserChannel.off();
            //reset the user channel (user or channel changed)
            setChannel(null);
        };
    }, [channelId, userId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the user doesn't have channels
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    const markAsRead = useCallback(() => {
        // marking as read by setting the USER_CHANNEL_UNSEEN_COUNT to 0
        return userChannelService.update(
            userId,
            channelId,
            {
                [USER_CHANNEL_UNSEEN_COUNT]: 0,
            }
        )
    }, [channelId, userId]);


    const saveDraftMessage = useCallback((message: string) => {
        // console.log('save draft message', userId, channelId, message)
        return userChannelService
            .update(
                //current one from before change
                userId,
                channelId,
                {
                    draft: {
                        message
                    }
                })
    }, [channelId, userId])

    return {
        channel,
        isLoading,
        error,
        markAsRead,
        saveDraftMessage
    }
}